import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, Trash, Ticket as TicketIcon, MagnifyingGlass, Warning, ChatCircle, CalendarBlank } from "@phosphor-icons/react";

const STATUS = [
  { key: "open",         label: "Open",         cls: "bg-slate-200 text-slate-800" },
  { key: "in_progress",  label: "In progress",  cls: "bg-blue-100 text-blue-800" },
  { key: "blocked",      label: "Blocked",      cls: "bg-red-100 text-red-800" },
  { key: "done",         label: "Done",         cls: "bg-emerald-100 text-emerald-800" },
];
const PRIORITY = [
  { key: "low",     label: "Low",    cls: "bg-slate-100 text-slate-700 border-slate-300" },
  { key: "medium",  label: "Medium", cls: "bg-blue-50 text-blue-700 border-blue-300" },
  { key: "high",    label: "High",   cls: "bg-amber-50 text-amber-800 border-amber-300" },
  { key: "urgent",  label: "Urgent", cls: "bg-red-50 text-red-800 border-red-300" },
];
const FILTERS = [
  { key: "all",         label: "All" },
  { key: "mine",        label: "Assigned to me" },
  { key: "overdue",     label: "Overdue" },
  { key: "open",        label: "Open" },
  { key: "done",        label: "Done" },
];

const statusMeta = (s) => STATUS.find(x => x.key === s) || STATUS[0];
const priorityMeta = (p) => PRIORITY.find(x => x.key === p) || PRIORITY[1];

export default function TicketsPage() {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const prefilledDeal = useMemo(() => {
    const hs = searchParams.get("deal");
    return hs ? { hs_id: hs, name: searchParams.get("deal_name") || "" } : null;
  }, [searchParams]);
  useEffect(() => {
    if (prefilledDeal) setCreating(true);
    const openParam = searchParams.get("open");
    if (openParam) setOpenId(openParam);
  }, []); // eslint-disable-line
  const clearQuery = () => setSearchParams({});
  const currentUserId = (() => {
    try { return JSON.parse(localStorage.getItem("sakshi_user") || "null")?.id || ""; } catch { return ""; }
  })();
  const isAdmin = (() => {
    try { return (JSON.parse(localStorage.getItem("sakshi_user") || "null")?.roles || []).includes("admin"); } catch { return false; }
  })();

  const load = async () => {
    setBusy(true);
    const params = {};
    if (filter === "mine") params.assignee = "me";
    else if (filter === "overdue") params.overdue_only = true;
    else if (filter === "open") params.status = "open";
    else if (filter === "done") params.status = "done";
    if (q.trim()) params.q = q.trim();
    try {
      const { data } = await api.get("/tickets", { params });
      setRows(data.tickets || []);
    } catch (e) { toast.error("Failed to load tickets"); }
    finally { setBusy(false); }
  };
  const loadStats = async () => { try { const { data } = await api.get("/tickets/stats"); setStats(data); } catch {} };
  const loadUsers = async () => { try { const { data } = await api.get("/users/assignable"); setUsers(data || []); } catch {} };

  // `load`, `loadStats`, `loadUsers` read `filter` / `q` from the enclosing
  // closure — which is refreshed every render — so intentionally omitting
  // them from the dep array does NOT cause stale-state reads. The eslint
  // suppressions document that omission is deliberate.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filter]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [q]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadStats(); loadUsers(); }, []);

  const sorted = useMemo(() => [...rows].sort((a, b) => {
    // Overdue first, then by updated_at desc
    if (a.overdue !== b.overdue) return a.overdue ? -1 : 1;
    return new Date(b.updated_at) - new Date(a.updated_at);
  }), [rows]);

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1400px]" data-testid="tickets-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="label-micro">Team · action items</div>
          <h1 className="font-display text-2xl sm:text-3xl font-black mt-1">Tickets</h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">Track work across the team — anyone can open a ticket, assign it, and comment. Overdue rows glow red.</p>
        </div>
        <button onClick={() => setCreating(true)} data-testid="new-ticket-btn"
                className="text-sm inline-flex items-center gap-1.5 bg-slate-900 hover:bg-blue-700 text-white rounded-sm px-4 py-2 transition-colors font-semibold">
          <Plus size={14} weight="bold"/>New ticket
        </button>
      </div>

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-5" data-testid="tickets-stats">
          <StatTile label="All open" value={stats.all_open} tone="slate"/>
          <StatTile label="Assigned to me" value={stats.mine_open} tone="blue"/>
          <StatTile label="Overdue" value={stats.overdue} tone={stats.overdue > 0 ? "red" : "slate"}/>
          <StatTile label="My overdue" value={stats.overdue_mine} tone={stats.overdue_mine > 0 ? "red" : "slate"}/>
          <StatTile label="Done · 7d" value={stats.done_this_week} tone="emerald"/>
        </div>
      )}

      <div className="mt-6 flex items-center gap-2 flex-wrap">
        <div className="inline-flex border border-slate-200 rounded-sm overflow-hidden">
          {FILTERS.map(f => (
            <button key={f.key} onClick={() => setFilter(f.key)}
                    data-testid={`ticket-filter-${f.key}`}
                    className={`text-xs font-semibold px-3 py-1.5 transition-colors ${filter === f.key ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>{f.label}</button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <MagnifyingGlass size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search tickets…"
                 data-testid="tickets-search"
                 className="w-full border border-slate-300 rounded-sm pl-9 pr-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>
        </div>
      </div>

      <div className="mt-4 bg-white border border-slate-200 rounded-sm">
        {busy && rows.length === 0 && <div className="p-10 text-center text-sm text-slate-500">Loading tickets…</div>}
        {!busy && sorted.length === 0 && <div className="p-10 text-center text-sm text-slate-500" data-testid="tickets-empty">No tickets match this filter. Hit <b>New ticket</b> to add one.</div>}
        {sorted.length > 0 && (
          <table className="w-full text-sm" data-testid="tickets-table">
            <thead>
              <tr className="text-left border-b border-slate-200">
                <th className="label-micro py-2 px-3">Title</th>
                <th className="label-micro py-2 px-3">Status</th>
                <th className="label-micro py-2 px-3">Priority</th>
                <th className="label-micro py-2 px-3">Assignee</th>
                <th className="label-micro py-2 px-3">Due</th>
                <th className="label-micro py-2 px-3">Updated</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(t => {
                const s = statusMeta(t.status); const p = priorityMeta(t.priority);
                return (
                  <tr key={t.id} onClick={() => setOpenId(t.id)} data-testid={`ticket-row-${t.id}`}
                      className={`border-b border-slate-100 cursor-pointer transition-colors ${t.overdue ? "bg-red-50 hover:bg-red-100" : "hover:bg-slate-50"}`}>
                    <td className="py-2.5 px-3">
                      <div className="flex items-center gap-2">
                        {t.overdue && <Warning size={13} weight="fill" className="text-red-600 shrink-0"/>}
                        <div className="min-w-0">
                          <div className="font-semibold text-slate-900 truncate">{t.title}</div>
                          <div className="text-[11px] text-slate-500 truncate flex items-center gap-1">
                            by {t.reporter_name}
                            {t._deal_name && <span className="ml-1 inline-flex items-center gap-0.5 text-blue-700 font-semibold" data-testid={`ticket-deal-chip-${t.id}`}>🔗 {t._deal_name}</span>}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="py-2.5 px-3"><span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${s.cls}`}>{s.label}</span></td>
                    <td className="py-2.5 px-3"><span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${p.cls}`}>{p.label}</span></td>
                    <td className="py-2.5 px-3 text-xs">
                      {t.assignee_name ? (
                        <div className="flex items-center gap-1.5">
                          <span className="w-5 h-5 rounded-full bg-slate-800 text-white grid place-items-center text-[9px] font-bold">{t.assignee_name.slice(0,1).toUpperCase()}</span>
                          <span className="truncate">{t.assignee_name}</span>
                        </div>
                      ) : <span className="text-slate-400 italic">Unassigned</span>}
                    </td>
                    <td className={`py-2.5 px-3 text-xs tabular-nums ${t.overdue ? "text-red-700 font-black" : "text-slate-700"}`}>{t.due_date || "—"}</td>
                    <td className="py-2.5 px-3 text-xs text-slate-500">{new Date(t.updated_at).toLocaleDateString()}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {creating && <TicketEditor users={users} currentUserId={currentUserId} prefilledDeal={prefilledDeal}
        onCancel={() => { setCreating(false); clearQuery(); }}
        onSaved={(t) => { setCreating(false); clearQuery(); setOpenId(t.id); load(); loadStats(); }}/>}
      {openId && <TicketDrawer id={openId} users={users} isAdmin={isAdmin} onClose={() => { setOpenId(null); clearQuery(); }} onChanged={() => { load(); loadStats(); }}/>}
    </div>
  );
}

export function InlineTicketEditor(props) { return <TicketEditor {...props}/>; }
export function InlineTicketDrawer(props) { return <TicketDrawer {...props}/>; }

function StatTile({ label, value, tone }) {
  const toneMap = { slate: "bg-slate-50 border-slate-200 text-slate-900", blue: "bg-blue-50 border-blue-200 text-blue-900", red: "bg-red-50 border-red-300 text-red-900", emerald: "bg-emerald-50 border-emerald-200 text-emerald-900" };
  return (
    <div className={`border rounded-sm p-3 ${toneMap[tone] || toneMap.slate}`} data-testid={`ticket-stat-${label.toLowerCase().replace(/[^a-z]/g, '-')}`}>
      <div className="label-micro">{label}</div>
      <div className="font-display font-black text-2xl mt-1 tabular-nums">{value}</div>
    </div>
  );
}

function TicketEditor({ users, currentUserId, prefilledDeal, onCancel, onSaved }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState("medium");
  const [status, setStatus] = useState("open");
  const [dueDate, setDueDate] = useState("");
  const [assigneeId, setAssigneeId] = useState(currentUserId);
  const [dealHsId, setDealHsId] = useState(prefilledDeal?.hs_id || "");
  const [dealName, setDealName] = useState(prefilledDeal?.name || "");
  const [dealSearch, setDealSearch] = useState("");
  const [dealOptions, setDealOptions] = useState([]);
  const [dealOpen, setDealOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!dealOpen) return;
    const t = setTimeout(async () => {
      try { const { data } = await api.get("/crm/deals", { params: { q: dealSearch, limit: 15 }});
        setDealOptions(data.items || []);
      } catch {}
    }, 200);
    return () => clearTimeout(t);
  }, [dealSearch, dealOpen]);

  const save = async () => {
    if (!title.trim()) { toast.error("Title required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/tickets", { title, description, priority, status,
        due_date: dueDate || null, assignee_user_id: assigneeId || "",
        related_deal_hs_id: dealHsId || null });
      toast.success("Ticket created");
      onSaved(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="ticket-editor-modal">
      <div className="absolute inset-0 bg-slate-900/50" onClick={onCancel}/>
      <div className="relative bg-white rounded-sm shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-3 flex items-center justify-between">
          <h2 className="font-display font-bold text-lg">New ticket</h2>
          <button onClick={onCancel} className="p-1.5 rounded-sm hover:bg-slate-100"><X size={16}/></button>
        </div>
        <div className="p-5 space-y-4">
          <label className="block">
            <span className="label-micro">Title <span className="text-red-600">*</span></span>
            <input value={title} onChange={(e) => setTitle(e.target.value)} autoFocus data-testid="ticket-title-input"
                   placeholder="What needs to be done?"
                   className="mt-1 w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>
          </label>
          <label className="block">
            <span className="label-micro">Description</span>
            <textarea rows={4} value={description} onChange={(e) => setDescription(e.target.value)} data-testid="ticket-desc-input"
                      className="mt-1 w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>
          </label>
          <div>
            <div className="label-micro">Related deal (optional)</div>
            {dealHsId ? (
              <div className="mt-1 flex items-center justify-between border border-slate-300 rounded-sm px-3 py-2 bg-blue-50" data-testid="ticket-deal-selected">
                <div className="text-sm font-semibold text-slate-800 truncate">🔗 {dealName || dealHsId}</div>
                <button onClick={() => { setDealHsId(""); setDealName(""); setDealOpen(true); }} className="text-xs text-slate-500 hover:text-red-600">Change</button>
              </div>
            ) : (
              <div className="relative">
                <input value={dealSearch} onChange={(e) => { setDealSearch(e.target.value); setDealOpen(true); }} onFocus={() => setDealOpen(true)}
                       placeholder="Search deals to link…" data-testid="ticket-deal-picker"
                       className="mt-1 w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>
                {dealOpen && (
                  <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-sm shadow-lg max-h-56 overflow-y-auto">
                    {dealOptions.length === 0 && <div className="px-3 py-2 text-xs text-slate-500">No deals match.</div>}
                    {dealOptions.map(d => (
                      <button key={d.hs_id} type="button" onClick={() => { setDealHsId(d.hs_id); setDealName(d.properties?.dealname || ""); setDealOpen(false); setDealSearch(""); }}
                              className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 border-b border-slate-100 last:border-0"
                              data-testid={`ticket-deal-opt-${d.hs_id}`}>
                        <div className="font-semibold text-slate-800 truncate">{d.properties?.dealname || "(Untitled)"}</div>
                        <div className="text-[11px] text-slate-500 truncate">{d._client_name || "—"}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <label className="block"><span className="label-micro">Status</span>
              <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="ticket-status-select"
                      className="mt-1 w-full border border-slate-300 rounded-sm px-2 py-2 text-sm">
                {STATUS.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}</select></label>
            <label className="block"><span className="label-micro">Priority</span>
              <select value={priority} onChange={(e) => setPriority(e.target.value)} data-testid="ticket-priority-select"
                      className="mt-1 w-full border border-slate-300 rounded-sm px-2 py-2 text-sm">
                {PRIORITY.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}</select></label>
            <label className="block"><span className="label-micro">Due date</span>
              <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} data-testid="ticket-due-input"
                     className="mt-1 w-full border border-slate-300 rounded-sm px-2 py-2 text-sm"/></label>
            <label className="block"><span className="label-micro">Assign to</span>
              <select value={assigneeId} onChange={(e) => setAssigneeId(e.target.value)} data-testid="ticket-assignee-select"
                      className="mt-1 w-full border border-slate-300 rounded-sm px-2 py-2 text-sm">
                <option value="">— Unassigned —</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.full_name || u.email}</option>)}</select></label>
          </div>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onCancel} className="text-sm border border-slate-300 rounded-sm px-4 py-2 hover:bg-slate-100">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="ticket-create-save"
                  className="text-sm bg-slate-900 hover:bg-blue-700 disabled:opacity-50 text-white rounded-sm px-4 py-2 transition-colors">
            {busy ? "Saving…" : "Create ticket"}
          </button>
        </div>
      </div>
    </div>
  );
}

function TicketDrawer({ id, users, isAdmin, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [comment, setComment] = useState("");
  const load = async () => {
    try { const { data } = await api.get(`/tickets/${id}`); setData(data); }
    catch (e) { toast.error("Failed to load ticket"); onClose(); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id]);

  const patch = async (payload) => {
    try {
      const { data: updated } = await api.patch(`/tickets/${id}`, payload);
      setData(prev => ({ ...prev, ticket: updated }));
      onChanged();
      // reload activities
      const { data: fresh } = await api.get(`/tickets/${id}`);
      setData(fresh);
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };
  const addComment = async () => {
    if (!comment.trim()) return;
    try {
      await api.post(`/tickets/${id}/comments`, { body: comment });
      setComment("");
      const { data: fresh } = await api.get(`/tickets/${id}`);
      setData(fresh);
      onChanged();
    } catch (e) { toast.error("Comment failed"); }
  };
  const del = async () => {
    if (!window.confirm("Delete this ticket? Comments will be lost.")) return;
    try { await api.delete(`/tickets/${id}`); toast.success("Deleted"); onChanged(); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  if (!data) return null;
  const t = data.ticket; const acts = data.activities || [];
  const s = statusMeta(t.status); const p = priorityMeta(t.priority);

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-slate-900/50" onClick={onClose}/>
      <aside className="absolute right-0 top-0 h-full w-full max-w-3xl bg-slate-50 shadow-2xl overflow-y-auto" data-testid="ticket-drawer">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-3 flex items-center justify-between z-10">
          <div className="flex items-center gap-2 min-w-0">
            <div className={`w-8 h-8 rounded-md grid place-items-center shrink-0 ${t.overdue ? "bg-red-100 text-red-700" : "bg-blue-100 text-blue-700"}`}><TicketIcon size={16} weight="fill"/></div>
            <div className="min-w-0">
              <div className="label-micro">Ticket · {t.id.slice(0, 8)}</div>
              <input value={t.title} onChange={(e) => setData(prev => ({ ...prev, ticket: { ...prev.ticket, title: e.target.value } }))}
                     onBlur={(e) => e.target.value !== t.title && patch({ title: e.target.value })}
                     data-testid="ticket-drawer-title"
                     className="font-display font-bold text-lg text-slate-900 bg-transparent focus:outline-none focus:bg-slate-50 rounded-sm px-1 -mx-1 w-full"/>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isAdmin && <button onClick={del} data-testid={`ticket-delete-${t.id}`} className="text-xs inline-flex items-center gap-1 border border-red-300 text-red-600 rounded-sm px-2.5 py-1.5 hover:bg-red-600 hover:text-white"><Trash size={12}/>Delete</button>}
            <button onClick={onClose} className="p-1.5 rounded-sm hover:bg-slate-100"><X size={16}/></button>
          </div>
        </div>

        {t.overdue && (
          <div className="bg-red-100 border-b border-red-200 px-5 py-2 text-xs text-red-800 font-semibold flex items-center gap-1.5" data-testid="ticket-overdue-banner">
            <Warning size={13} weight="fill"/>Overdue since {t.due_date}. Update the due date or move to Done to clear.
          </div>
        )}
        {t._deal_name && (
          <div className="bg-blue-50 border-b border-blue-200 px-5 py-2 text-xs text-blue-800 flex items-center gap-1.5" data-testid="ticket-linked-deal">
            🔗 Linked to deal <a href={`/crm?deal=${t.related_deal_hs_id}`} className="font-semibold underline">{t._deal_name}</a>
            {t._deal_amount && <span className="text-slate-500">· ₹{Number(t._deal_amount).toLocaleString("en-IN")}</span>}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-5">
          <div className="md:col-span-2 space-y-4">
            <div className="bg-white rounded-md border border-slate-200 p-4">
              <div className="label-micro mb-1.5">Description</div>
              <textarea rows={4} defaultValue={t.description || ""}
                        onBlur={(e) => e.target.value !== (t.description || "") && patch({ description: e.target.value })}
                        data-testid="ticket-drawer-desc"
                        placeholder="Add context…"
                        className="w-full text-sm text-slate-800 bg-transparent focus:outline-none focus:bg-slate-50 rounded-sm p-1"/>
            </div>

            <div className="bg-white rounded-md border border-slate-200">
              <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
                <ChatCircle size={14} weight="bold"/>
                <div className="font-semibold text-sm">Activity</div>
                <div className="text-[11px] text-slate-500 ml-auto">{acts.length} entries</div>
              </div>
              <div className="p-4 border-b border-slate-100 bg-amber-50/40">
                <textarea rows={2} value={comment} onChange={(e) => setComment(e.target.value)}
                          placeholder="Leave a comment…" data-testid="ticket-comment-input"
                          className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"/>
                <div className="flex justify-end mt-2">
                  <button onClick={addComment} disabled={!comment.trim()} data-testid="ticket-comment-add"
                          className="text-xs bg-slate-900 hover:bg-blue-700 disabled:opacity-40 text-white rounded-sm px-4 py-2 font-semibold">Post comment</button>
                </div>
              </div>
              <ul className="divide-y divide-slate-100" data-testid="ticket-activity-list">
                {acts.length === 0 && <li className="p-6 text-xs text-slate-500 italic text-center">No activity yet — be the first to comment.</li>}
                {acts.map(a => (
                  <li key={a.id} className="p-4 flex gap-3" data-testid={`ticket-activity-${a.id}`}>
                    <div className={`w-7 h-7 rounded-full grid place-items-center shrink-0 text-[10px] font-bold ${a.kind === "comment" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-700"}`}>
                      {(a.author_name || "?").slice(0, 1).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[11px] text-slate-500">
                        <b className="text-slate-800">{a.author_name}</b>
                        {a.kind !== "comment" && <span className="text-slate-500"> · {a.kind.replace(/_/g, " ")}</span>}
                        <span className="text-slate-400"> · {new Date(a.at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</span>
                      </div>
                      <div className={`text-sm mt-0.5 whitespace-pre-wrap ${a.kind === "comment" ? "text-slate-800" : "text-slate-600 italic"}`}>{a.body}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Right rail */}
          <div className="space-y-4">
            <div className="bg-white rounded-md border border-slate-200 p-4 space-y-3">
              <SelectRow label="Status" testid="ticket-drawer-status" value={t.status} options={STATUS.map(s => ({ value: s.key, label: s.label }))} onChange={(v) => patch({ status: v })} pillCls={s.cls} pillLabel={s.label}/>
              <SelectRow label="Priority" testid="ticket-drawer-priority" value={t.priority} options={PRIORITY.map(p => ({ value: p.key, label: p.label }))} onChange={(v) => patch({ priority: v })} pillCls={p.cls + " border"} pillLabel={p.label}/>
              <div>
                <div className="label-micro mb-1">Assignee</div>
                <select value={t.assignee_user_id || ""} onChange={(e) => patch({ assignee_user_id: e.target.value })} data-testid="ticket-drawer-assignee"
                        className="w-full border border-slate-300 rounded-sm px-2 py-2 text-sm">
                  <option value="">— Unassigned —</option>
                  {users.map(u => <option key={u.id} value={u.id}>{u.full_name || u.email}</option>)}
                </select>
              </div>
              <div>
                <div className="label-micro mb-1 flex items-center gap-1"><CalendarBlank size={11}/>Due date</div>
                <input type="date" value={t.due_date || ""} onChange={(e) => patch({ due_date: e.target.value || null })} data-testid="ticket-drawer-due"
                       className={`w-full border rounded-sm px-2 py-2 text-sm ${t.overdue ? "border-red-400 text-red-700 font-semibold" : "border-slate-300"}`}/>
              </div>
            </div>
            <div className="bg-white rounded-md border border-slate-200 p-4">
              <div className="label-micro">Reported by</div>
              <div className="flex items-center gap-2 mt-1.5">
                <span className="w-6 h-6 rounded-full bg-slate-800 text-white grid place-items-center text-[10px] font-bold">{(t.reporter_name || "?").slice(0,1).toUpperCase()}</span>
                <div className="text-sm font-semibold text-slate-800 truncate">{t.reporter_name}</div>
              </div>
              <div className="label-micro mt-3">Created</div>
              <div className="text-xs text-slate-600 mt-0.5">{new Date(t.created_at).toLocaleString()}</div>
              <div className="label-micro mt-3">Last updated</div>
              <div className="text-xs text-slate-600 mt-0.5">{new Date(t.updated_at).toLocaleString()}</div>
              {t.completed_at && <><div className="label-micro mt-3">Completed</div><div className="text-xs text-emerald-700 font-semibold mt-0.5">{new Date(t.completed_at).toLocaleString()}</div></>}
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
}

function SelectRow({ label, value, options, onChange, pillCls, pillLabel, testid }) {
  return (
    <div>
      <div className="label-micro mb-1 flex items-center justify-between">
        <span>{label}</span>
        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${pillCls}`}>{pillLabel}</span>
      </div>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
              className="w-full border border-slate-300 rounded-sm px-2 py-2 text-sm">
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}
